import React, { useEffect, useState } from 'react';
import {
  Box,
  CircularProgress,
  Typography,
  IconButton,
  useMediaQuery,
  useTheme,
  Fade,
  Slide,
  Paper
} from '@mui/material';
import {
  Fullscreen,
  FullscreenExit,
  CallEnd,
  Minimize,
  Close,
  Videocam,
  Mic
} from '@mui/icons-material';
import useFetchUser from 'hooks/useFetchUser';
import VideoCallView from './VideoCallView';
import useUserId from 'hooks/useUserId';
import './VideoCall.css';

const apiUrlMiror = process.env.REACT_APP_API_MIROR_URL;

function VideoCallComponent({ setMessage, receiverId, senderId, handleMessageSend, setSelectedContactId, choosenContacts,
  setVideoCallLaunched, meetingLink, setMeetingLink, callData, end_Call, setCallData, setFromOtherPage, setCallModalOpen }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [isMinimized, setIsMinimized] = useState(false);
  const [isFullScreen, setIsFullScreen] = useState(true);
  const [connectionStatus, setConnectionStatus] = useState('connecting');

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const apiUrl = process.env.REACT_APP_API_URL;
  const { formData } = useFetchUser();
  const userId = useUserId();
  // Handler functions for video call states
  const handleMinimize = () => {
    if (isMobile) {
      setIsMinimized(true);
      setIsFullScreen(false);
    }
  };

  const handleMaximize = () => {
    setIsMinimized(false);
    setIsFullScreen(true);
  };

  const handleEndCall = () => {
    console.log('🔴 End call button clicked');

    // Call end_Call to notify other participants via WebSocket
    if (end_Call) {
      end_Call();
    }

    // Reset the meeting link so a fresh one is generated for the next call
    // This is critical - without this, the next call might try to use the old room
    if (setMeetingLink) {
      console.log('🔄 Resetting meeting link for next call');
      setMeetingLink("");
    }

    // Reset call data to clear any stale call information
    if (setCallData) {
      setCallData(null);
    }

    // Note: videoCallLaunched will be set to false by leaveRoom() in VideoCallView
    // which is called during cleanup, so we don't need to set it here
    setIsMinimized(false);
    setIsFullScreen(true);
  };

  // Handle back navigation on mobile
  useEffect(() => {
    const handleBackButton = (event) => {
      if (isMobile && isFullScreen && !isMinimized) {
        event.preventDefault();
        handleMinimize();
      }
    };

    if (isMobile) {
      window.addEventListener('popstate', handleBackButton);
      return () => window.removeEventListener('popstate', handleBackButton);
    }
  }, [isMobile, isFullScreen, isMinimized]);

  // Prevent body scroll and interaction on mobile when video call is in full-screen mode
  useEffect(() => {
    if (isMobile && isFullScreen && !isMinimized) {
      // Store original styles for both html and body
      const originalBodyOverflow = document.body.style.overflow;
      const originalBodyPosition = document.body.style.position;
      const originalBodyWidth = document.body.style.width;
      const originalBodyHeight = document.body.style.height;
      const originalHtmlOverflow = document.documentElement.style.overflow;
      const originalHtmlHeight = document.documentElement.style.height;

      // Apply full-screen overlay styles to prevent any scrolling or interaction
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
      document.body.style.height = '100%';
      document.body.style.top = '0';
      document.body.style.left = '0';
      document.body.classList.add('video-call-active');

      document.documentElement.style.overflow = 'hidden';
      document.documentElement.style.height = '100%';
      document.documentElement.classList.add('video-call-active');

      // Cleanup function to restore original styles
      return () => {
        document.body.style.overflow = originalBodyOverflow;
        document.body.style.position = originalBodyPosition;
        document.body.style.width = originalBodyWidth;
        document.body.style.height = originalBodyHeight;
        document.body.style.top = '';
        document.body.style.left = '';
        document.body.classList.remove('video-call-active');

        document.documentElement.style.overflow = originalHtmlOverflow;
        document.documentElement.style.height = originalHtmlHeight;
        document.documentElement.classList.remove('video-call-active');
      };
    } else {
      // Ensure cleanup when not in full-screen mode
      document.body.classList.remove('video-call-active');
      document.documentElement.classList.remove('video-call-active');
    }
  }, [isMobile, isFullScreen, isMinimized]);

  // Monitor connection status
  useEffect(() => {
    const timer = setTimeout(() => {
      setConnectionStatus('connected');
    }, 2000);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    // Clear location state
    window.history.replaceState({}, document.title);

    // Clear any stored call data
    sessionStorage.removeItem('incomingCallData');

    // Cleanup function to reset state when component unmounts
    // This ensures a fresh state for the next call
    return () => {
      console.log('🧹 VideoCallComponent unmounting - cleaning up...');
      
      // Reset meeting link so next call gets a fresh one
      if (setMeetingLink) {
        console.log('🔄 Resetting meeting link on unmount');
        setMeetingLink("");
      }
      
      // Reset call data
      if (setCallData) {
        setCallData(null);
      }
      
      // Clear any stored call data in session storage
      sessionStorage.removeItem('incomingCallData');
    };
  }, [setMeetingLink, setCallData]);
  const sendUserIdToExpress = async (userId) => {
    try {
      // const response = await fetch('http://localhost:3001/post_receiver_id', {
      const response = await fetch(`${apiUrlMiror}post_receiver_id`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ userId }),
      });

      if (!response.ok) {
        throw new Error('Failed to send user ID to Express server');
      }

      const data = await response.json();
      console.log('User ID sent successfully:', data);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  useEffect(() => {
    console.log('start call', { receiverId, senderId, callData });
    const createGroupLink = async () => {
      setLoading(true);
      setError('');
      try {
        console.log('Creating group link with:', { receiverId, senderId, apiUrl });
        const response = await fetch(
          `${apiUrl}messaging/api/chat/${receiverId}/${senderId}/creategroup/`
        );
        console.log('Response status:', response.status);
        if (!response.ok) {
          throw new Error(`Failed to generate meeting link: ${response.status} ${response.statusText}`);
        }
        const result = await response.json();
        console.log('Meeting link result:', result);
        setMeetingLink(result.meeting_link);
        console.log('meeting link is:', result.meeting_link);
      } catch (error) {
        console.error('Error in createGroupLink:', error);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    if (!callData && receiverId && senderId) {
      createGroupLink();
    } else if (callData && callData.meetingLink) {
      setMeetingLink(callData.meetingLink);
    } else {
      console.log('Missing required data:', { callData, receiverId, senderId });
    }

  }, [receiverId, senderId, callData, apiUrl]);

  useEffect(() => {
    if (meetingLink) {
      console.log('users info:', formData);
      console.log('meetingLink::', meetingLink);
      const sendMessageAndInitialize = async () => {
        const message = `${formData.last_name} ${formData.first_name} has started a call with you. You can join the call at: ${meetingLink}`;
        setMessage(message);

        await new Promise((resolve) => setTimeout(resolve, 100));

        const messageSent = await handleMessageSend();

        if (messageSent) {
          const userName = `${formData.first_name} ${formData.last_name}`;

        }
      };
      // sendMessageAndInitialize();
      sendUserIdToExpress(formData.id);
    }
  }, [meetingLink])

  if (loading) {
    return (
      <Box
        sx={{
          position: isMobile ? 'fixed' : 'relative',
          top: isMobile ? 0 : 'auto',
          left: isMobile ? 0 : 'auto',
          width: isMobile ? '100%' : '100%',
          height: isMobile ? '100%' : '400px',
          zIndex: isMobile ? 9999 : 'auto',
          bgcolor: isMobile ? '#000' : 'background.paper',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          flexDirection: 'column',
          gap: 2
        }}
      >
        <CircularProgress sx={{ color: 'white' }} />
        <Typography sx={{ color: 'white' }}>Creating meeting room...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          position: isMobile ? 'fixed' : 'relative',
          top: isMobile ? 0 : 'auto',
          left: isMobile ? 0 : 'auto',
          width: isMobile ? '100%' : '100%',
          height: isMobile ? '100%' : '400px',
          zIndex: isMobile ? 9999 : 'auto',
          bgcolor: isMobile ? '#000' : 'background.paper',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          flexDirection: 'column',
          gap: 2
        }}
      >
        <Typography color="error" sx={{ color: '#ff6b6b', textAlign: 'center', px: 2 }}>
          {error}
        </Typography>
        <Typography sx={{ color: 'white', fontSize: '0.875rem' }}>
          Please try again or contact support
        </Typography>
      </Box>
    );
  }

  if (!meetingLink) {
    return (
      <Box
        sx={{
          position: isMobile ? 'fixed' : 'relative',
          top: isMobile ? 0 : 'auto',
          left: isMobile ? 0 : 'auto',
          width: isMobile ? '100%' : '100%',
          height: isMobile ? '100%' : '400px',
          zIndex: isMobile ? 9999 : 'auto',
          bgcolor: isMobile ? '#000' : 'background.paper',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          flexDirection: 'column',
          gap: 2
        }}
      >
        <CircularProgress sx={{ color: 'white' }} />
        <Typography sx={{ color: 'white' }}>Initializing meeting...</Typography>
        <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.875rem' }}>
          Setting up video call room
        </Typography>
      </Box>
    );
  }

  // Single persistent video call container that transitions between states
  const renderVideoCall = () => {
    const isFullScreenMode = isMobile ? isFullScreen && !isMinimized : true;
    const isMiniMode = isMobile && isMinimized;

    return (
      <Box
        sx={{
          // Dynamic positioning based on state
          position: isMobile ? 'fixed' : 'relative',
          top: isMiniMode ? 'auto' : (isMobile ? 0 : 'auto'),
          left: isMiniMode ? 'auto' : (isMobile ? 0 : 'auto'),
          bottom: isMiniMode ? 16 : 'auto',
          right: isMiniMode ? 16 : 'auto',

          // Dynamic sizing based on state
          width: isMiniMode ? 280 : (isMobile ? '100%' : '100%'),
          height: isMiniMode ? 200 : (isMobile ? '100%' : '80vh'),

          // Highest z-index for mobile to guarantee it's above everything
          zIndex: isMobile ? (isMiniMode ? 10000 : 9999) : 'auto',

          // Styling
          overflow: 'hidden',
          bgcolor: isMiniMode ? '#000' : (isMobile ? '#000' : 'background.paper'),
          borderRadius: isMiniMode ? 2 : 0,
          border: isMiniMode ? '2px solid' : 'none',
          borderColor: isMiniMode ? 'primary.main' : 'transparent',
          boxShadow: isMiniMode ? '0 8px 32px rgba(0,0,0,0.24)' : 'none',

          // Smooth transitions
          transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',

          // Mini window hover effect
          '&:hover': isMiniMode ? {
            transform: 'scale(1.02)',
            boxShadow: '0 12px 40px rgba(0,0,0,0.32)',
          } : {},
        }}
      >
        {/* Header - shown in both full-screen mobile and mini modes */}
        {(isMobile || isMiniMode) && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: isMiniMode ? 40 : 60,
              bgcolor: 'rgba(0,0,0,0.7)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: isMiniMode ? 1 : 2,
              zIndex: 10,
              backdropFilter: isMiniMode ? 'none' : 'blur(10px)',
            }}
          >
            <Typography
              variant={isMiniMode ? "caption" : "h6"}
              sx={{
                color: 'white',
                fontWeight: 500,
                fontSize: isMiniMode ? '0.75rem' : '1.125rem'
              }}
            >
              Video Call
            </Typography>
            <Box sx={{ display: 'flex', gap: isMiniMode ? 0.5 : 1 }}>
              {/* Full Screen / Minimize Button */}
              <IconButton
                size={isMiniMode ? "small" : "medium"}
                onClick={isMiniMode ? handleMaximize : handleMinimize}
                sx={{
                  color: 'white',
                  bgcolor: 'rgba(255,255,255,0.1)',
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' },
                }}
              >
                {isMiniMode ? (
                  <Fullscreen fontSize="small" />
                ) : (
                  <Minimize fontSize="small" />
                )}
              </IconButton>

              {/* End Call Button */}
              <IconButton
                size={isMiniMode ? "small" : "medium"}
                onClick={handleEndCall}
                sx={{
                  color: 'white',
                  bgcolor: 'rgba(244,67,54,0.8)',
                  '&:hover': { bgcolor: 'rgba(244,67,54,1)' },
                }}
              >
                <CallEnd fontSize="small" />
              </IconButton>
            </Box>
          </Box>
        )}

        {/* Connection status */}
        {connectionStatus !== 'connected' && (
          <Box
            sx={{
              position: 'absolute',
              top: isMiniMode ? 45 : 70,
              left: isMiniMode ? 8 : 16,
              bgcolor: connectionStatus === 'connecting' ? 'warning.main' : 'error.main',
              color: 'white',
              px: isMiniMode ? 1 : 1.5,
              py: 0.5,
              borderRadius: 1,
              fontSize: isMiniMode ? '0.6875rem' : '0.75rem',
              fontWeight: 500,
              zIndex: 11,
            }}
          >
            {connectionStatus === 'connecting' ? 'Connecting...' : 'Disconnected'}
          </Box>
        )}

        {/* Desktop video call view - only show when not mobile */}
        {!isMobile && (
          <VideoCallView
            senderId={senderId}
            setCallModalOpen={setCallModalOpen}
            setFromOtherPage={setFromOtherPage}
            meetingLink={meetingLink}
            callData={callData}
            setCallData={setCallData}
            choosenContacts={choosenContacts}
            setSelectedContactId={setSelectedContactId}
            end_Call={end_Call}
          />
        )}

        {/* Video iframe - always present */}
        <iframe
          src={`${meetingLink}?username=${encodeURIComponent(`${formData.first_name} ${formData.last_name}`)}&userId=${encodeURIComponent(userId.userId)}&roomId=${encodeURIComponent(meetingLink)}`}
          style={{
            width: '100%',
            height: '100%',
            border: 'none',
            marginTop: (isMobile || isMiniMode) ? (isMiniMode ? '40px' : '60px') : '0',
            height: (isMobile || isMiniMode) ? (isMiniMode ? 'calc(100% - 40px)' : 'calc(100% - 60px)') : '100%',
          }}
          allow="camera; microphone; display-capture; fullscreen"
          allowFullScreen
        />
      </Box>
    );
  };

  return renderVideoCall();
}

export default VideoCallComponent;